import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useAppSelector } from '../../../../store/hooks';
import { Row } from '@/components/ui/layout/Row';
import { Column } from '@/components/ui/layout/Column';
import { Text } from '@/components/ui/base/Text';

export const Dropzone = () => {
  const { uploadFiles, isUploading } = useFileUpload();
  const { uploadProgress, currentFile, totalFiles, uploadedFiles } =
    useAppSelector((state) => state.loading);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // Traiter les fichiers sélectionnés via clic
      if (acceptedFiles.length > 0) {
        await uploadFiles(acceptedFiles);
      }
    },
    [uploadFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/gif': ['.gif'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/bmp': ['.bmp'],
      'image/tiff': ['.tiff'],
      'image/aces': ['.exr'],
      'image/exr': ['.exr'],
      'image/x-portable-bitmap': ['.pbm'],
      'image/x-portable-graymap': ['.pgm'],
      'image/x-portable-pixmap': ['.ppm'],
      'image/x-cmu-raster': ['.rast'],
      'image/x-xbitmap': ['.xbm'],
      'image/x-rgb': ['.rgb'],
      'text/plain': ['.txt'],
      'text/html': ['.html', '.htm'],
    },
    disabled: isUploading,
    maxSize: 10 * 1024 * 1024,
    noKeyboard: true, // Désactiver le clavier pour éviter les conflits
  });

  return (
    <div
      {...getRootProps()}
      onDragEnter={(e) => e.stopPropagation()}
      onDragOver={(e) => e.stopPropagation()}
      onDragLeave={(e) => e.stopPropagation()}
      onDrop={(e) => e.stopPropagation()}
      className={`dropzone flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
        isDragActive
          ? 'border-primary bg-primary/10 dark:border-primary dark:bg-primary/10'
          : 'border-gray-300 bg-primary/5 hover:border-primary dark:border-gray-600 dark:bg-primary/5 dark:hover:border-primary'
      } ${isUploading ? 'pointer-events-none max-h-24 cursor-not-allowed opacity-50' : 'max-h-20'} p-8 text-center`}
    >
      <input {...getInputProps()} />
      {isUploading ? (
        <Column width="full" align="center" justify="center" gap="xs">
          <Row justify="center">
            <div className="mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Upload en cours...
            </div>
            {currentFile && (
              <Text size="xs" className="text-gray-600 dark:text-gray-300">
                {currentFile}
              </Text>
            )}
          </Row>

          <Text size="xs" className="text-gray-500 dark:text-gray-400">
            {totalFiles > 1 && `${uploadedFiles} / ${totalFiles} fichiers - `}
            {Math.round(uploadProgress)}% terminé
          </Text>

          <Row width="full" className="mb-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </Row>
        </Column>
      ) : isDragActive ? (
        <div>
          <p className="text-primary dark:text-primary">
            Déposez les fichiers ou liens PDF ici...
          </p>
        </div>
      ) : (
        <div>
          <p className="text-gray-600 dark:text-gray-300">
            Glissez-déposez des fichiers ou des liens PDF ici, ou cliquez pour
            sélectionner
          </p>
        </div>
      )}
    </div>
  );
};
